import { X, Scale, Loader2, Building2, Pill } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCompare, MAX_COMPARE_ITEMS } from "@/context/CompareContext";

/**
 * CompareDock - Phase 27 Bi-Modal Update
 * 
 * Color-coded chips based on entity type:
 * - Blue (#2A5D9E) for Assets (drugs, targets)
 * - Orange (#E67E22) for Companies
 * 
 * Like-to-Like comparison is enforced in CompareContext.
 */
export default function CompareDock() {
  const {
    compareItems,
    removeFromCompare,
    clearCompare,
    openCompareModal,
    isLoading,
    getCurrentEntityType,
  } = useCompare();

  if (compareItems.length === 0) return null;
  
  const currentEntityType = getCurrentEntityType();

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-50 pointer-events-none"
      data-testid="compare-dock"
    >
      <div className="max-w-3xl mx-auto px-4 pb-4">
        <div className="bg-slate-900 text-white rounded-t-lg shadow-sm border border-slate-700 pointer-events-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <Scale size={14} strokeWidth={1.5} className="text-slate-400" />
              <span className="font-mono text-[10px] text-slate-400 uppercase tracking-widest">
                Compare Mode
              </span>
              {/* Phase 27: Entity type indicator */}
              {currentEntityType && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-medium ${
                  currentEntityType === "company" 
                    ? "bg-[#E67E22]/20 text-[#E67E22]" 
                    : "bg-[#2A5D9E]/20 text-[#2A5D9E]"
                }`}>
                  {currentEntityType === "company" ? "Companies" : "Assets"}
                </span>
              )}
            </div>
            <button
              onClick={clearCompare}
              className="text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
              data-testid="clear-compare-btn"
            >
              Clear All
            </button>
          </div>

          {/* Items */}
          <div className="flex items-center gap-2 px-4 py-3">
            {/* Selected Items - Phase 27: Color-coded by entity type */}
            <div className="flex items-center gap-2 flex-1">
              {compareItems.map((item) => {
                const isCompany = item.entityType === "company";
                const borderColor = isCompany ? "border-[#E67E22]" : "border-[#2A5D9E]";
                const bgColor = isCompany ? "bg-[#E67E22]/10" : "bg-[#2A5D9E]/10";
                const textColor = isCompany ? "text-[#E67E22]" : "text-[#2A5D9E]";
                
                return (
                  <div
                    key={item.query}
                    className={`group flex items-center gap-2 ${bgColor} border ${borderColor} rounded-sm px-3 py-1.5`}
                    data-testid={`compare-item-${item.query.toLowerCase()}`}
                  >
                    {/* Entity type icon */}
                    {isCompany ? (
                      <Building2 size={12} className={textColor} />
                    ) : (
                      <Pill size={12} className={textColor} />
                    )}
                    <span className="font-mono text-xs text-white">
                      {item.query}
                    </span>
                    <button
                      onClick={() => removeFromCompare(item.query)}
                      className="text-slate-500 hover:text-white transition-colors"
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })}

              {/* Empty Slots - Phase 27: Show hint about entity type */}
              {Array.from({ length: MAX_COMPARE_ITEMS - compareItems.length }).map(
                (_, idx) => (
                  <div
                    key={`empty-${idx}`}
                    className="flex items-center gap-2 border border-dashed border-slate-600 rounded-sm px-3 py-1.5 opacity-40"
                  >
                    <span className="font-mono text-xs text-slate-500">
                      {currentEntityType 
                        ? `+ Add ${currentEntityType === "company" ? "company" : "asset"}`
                        : "+ Add item"
                      }
                    </span>
                  </div>
                )
              )}
            </div>

            {/* Compare Button */}
            <Button
              onClick={openCompareModal}
              disabled={compareItems.length < 2 || isLoading}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-sm font-mono text-xs h-8 px-4 disabled:opacity-50"
              data-testid="open-compare-modal-btn"
            >
              {isLoading ? (
                <>
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  Compare ({compareItems.length})
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
